import { translate } from "app/filters/i18nFilter";
import I18n from "app/pages/sales-intelligence/common-components/i18n/I18n";
import { formatNumberWithCommas } from "app/pages/sales-intelligence/utils/numbers";
import { createPluralHelperForI18nKey } from "app/pages/sales-intelligence/utils/strings";
import type { MutableRefObject, ReactElement, ReactNode } from "react";
import { forwardRef } from "react";
import { OpportunityListTypeEnum } from "../../../types";
import {
    StyledAutoCreatedTag,
    StyledItemBody,
    StyledItemIconWrapper,
    StyledItemInfoSection,
    StyledItemTitleText,
    StyledItemWrapper,
    StyledPrimaryContent,
    StyledSecondaryText,
} from "./styles";

export type OpportunityListItemProps = {
    className?: string;
    dataAutomationType: string;
    icon: ReactElement;
    index?: number;
    listName: string;
    listType: OpportunityListTypeEnum;
    numberOfWebsites: number;
    onClick?(): void;
    onMouseEnter?(): void;
    onMouseLeave?(): void;
    renderPrimaryContent?(): ReactNode;
    renderSecondaryContent?(): ReactNode;
};

const OpportunityListItem = (props: OpportunityListItemProps, ref: MutableRefObject<HTMLDivElement>) => {
    const {
        className,
        dataAutomationType,
        icon,
        index,
        listName,
        listType,
        numberOfWebsites,
        onClick,
        onMouseEnter,
        onMouseLeave,
        renderPrimaryContent,
        renderSecondaryContent,
    } = props;
    const getNumberOfWebsitesKey = createPluralHelperForI18nKey("si.common.number_of_websites");

    return (
        <StyledItemWrapper
            ref={ref}
            index={index}
            alignItems="center"
            className={className}
            data-automation="opportunity-list-item"
            data-automation-type={dataAutomationType}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
            onClick={onClick}
        >
            <StyledItemIconWrapper>{icon}</StyledItemIconWrapper>
            <StyledItemBody>
                <StyledItemTitleText data-automation="list-name-text">
                    <span>
                        {listType === OpportunityListTypeEnum.RecentlyViewed
                            ? translate("si.opportunity-list.auto-created.title")
                            : listName}
                    </span>
                    {(listType === OpportunityListTypeEnum.RecentlyViewed ||
                        listType === OpportunityListTypeEnum.Crm) ? <StyledAutoCreatedTag /> : null}
                </StyledItemTitleText>
                <StyledItemInfoSection alignItems="center">
                    <I18n
                        data-automation="number-of-websites-text"
                        element={StyledSecondaryText}
                        translatable={{
                            key: getNumberOfWebsitesKey(numberOfWebsites),
                            replacements: {
                                numberOfWebsites: formatNumberWithCommas(numberOfWebsites),
                            },
                        }}
                    />
                    {renderSecondaryContent?.()}
                </StyledItemInfoSection>
            </StyledItemBody>
            <StyledPrimaryContent>{renderPrimaryContent?.()}</StyledPrimaryContent>
        </StyledItemWrapper>
    );
}

export default forwardRef(OpportunityListItem);
